import { useState, useCallback } from 'react';
import { complete, completeChat, getAIServiceErrorMessage, type AIMessage, type AICompletionOptions } from '@/services/ai/ai-service';
import { isAIConfigured } from '@/services/ai/ai-config';
import { AIServiceError } from '@/services/ai/ai-service';

export interface UseAIOptions {
  onError?: (error: Error) => void;
}

export function useAI(options: UseAIOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const callAI = useCallback(async (
    prompt: string,
    systemPrompt?: string,
    aiOptions: AICompletionOptions = {}
  ): Promise<string | null> => {
    if (!isAIConfigured()) {
      const err = new AIServiceError(
        'AI is not configured. Please set your API key in Settings.',
        undefined,
        'AI_NOT_CONFIGURED'
      );
      setError(err);
      options.onError?.(err);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await complete(prompt, systemPrompt, aiOptions);
      return response;
    } catch (err) {
      const message = getAIServiceErrorMessage(err);
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      const friendlyError = new Error(message);
      (friendlyError as any).cause = error;
      if (err instanceof AIServiceError) (friendlyError as any).code = err.code;
      setError(friendlyError);
      options.onError?.(friendlyError);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  const callAIChat = useCallback(async (
    messages: AIMessage[],
    aiOptions: AICompletionOptions = {}
  ): Promise<string | null> => {
    if (!isAIConfigured()) {
      const err = new AIServiceError(
        'AI is not configured. Please set your API key in Settings.',
        undefined,
        'AI_NOT_CONFIGURED'
      );
      setError(err);
      options.onError?.(err);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await completeChat(messages, aiOptions);
      return response.content;
    } catch (err) {
      const message = getAIServiceErrorMessage(err);
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      const friendlyError = new Error(message);
      (friendlyError as any).cause = error;
      if (err instanceof AIServiceError) (friendlyError as any).code = err.code;
      setError(friendlyError);
      options.onError?.(friendlyError);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    callAI,
    callAIChat,
    isLoading,
    error,
    clearError,
    isConfigured: isAIConfigured()
  };
}
